import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import { Box, Chip } from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import MicrospecDataTable, { MicrospecDataTableColumn } from "../../../../MicrospecDynamicUI/Components/DataTable/MicrospecFirestoreDataTable";
import { useMicrospecDynamicUIContext } from "../../../../MicrospecDynamicUI/GlobalProvider";
import { useMicrospecThemeLanguageContext } from "../../../../MicrospecDynamicUI/Themes";
import { deleteDocumentById } from "../../../../MicrospecDynamicUI/Helpers/dataUtils";
import { BRANCH_ID, ORG_ID } from "../../../constants/collectionPaths";

const CategoriesManagementPage: React.FC = () => {
    const navigate = useNavigate();
    const { translate } = useMicrospecThemeLanguageContext().localizationProvider;
    const organizationId = ORG_ID;
    const branchId = BRANCH_ID;
    const { cloudProvider } = useMicrospecDynamicUIContext();
    const { firestore, userProfile } = cloudProvider;
    const { methods } = userProfile;
    const { getEntitiesRelationsCount } = methods;
    const db = firestore?.instance;
    const t = translate;
    const [refreshKey, setRefreshKey] = useState(0);

    const collectionPath = `organizations/${organizationId}/branches/${branchId}/categories`;
    const productsCollectionPath = `organizations/${organizationId}/branches/${branchId}/products`;

    // Define columns for the data table
    const columns: MicrospecDataTableColumn[] = [
        {
            field: 'name',
            headerName: t('category.name'),
            sortable: true,
            minWidth: 200,
            align: 'left',
            render: (value: string | { [locale: string]: string }) => {
                if (typeof value === 'string') return value;
                return translate(value);
            }
        },
        {
            field: 'description',
            headerName: t('category.description'),
            sortable: false,
            minWidth: 250,
            align: 'left',
            render: (value: string | { [locale: string]: string }) => {
                if (!value) return '-';
                if (typeof value === 'string') return value;
                return translate(value);
            }
        },
        {
            field: 'order',
            headerName: t('category.order'),
            sortable: true,
            minWidth: 100,
            align: 'center',
        },
        {
            field: 'is_published',
            headerName: t('category.published'),
            sortable: true,
            minWidth: 120,
            align: 'center',
            render: (value: boolean) => (
                <Chip
                    label={value ? t('category.published') : t('category.draft')}
                    color={value ? 'success' : 'default'}
                    size="small"
                    variant="outlined"
                />
            ),
        },
    ];

    // Handle delete category
    const handleDelete = async (row: any) => {
        if (!db || !organizationId || !branchId) return;

        await getEntitiesRelationsCount([
            { parentEntityId: row.id, childEntityCollection: collectionPath, childFieldName: 'parent_category_id' },
            { parentEntityId: row.id, childEntityCollection: productsCollectionPath, childFieldName: 'category_id' }
        ]).then(async (response: { results: { count: number }[] }) => {
            const results = response.results;
            const subcategoriesResult = results[0];
            const productsResult = results[1];

            if (subcategoriesResult.count > 0) {
                alert(t('category.hasSubcategories'));
                return;
            } else if (productsResult.count > 0) {
                alert(t('category.hasProducts'));
                return;
            } else {
                if (window.confirm(t('category.confirmDelete'))) {
                    try {
                        await deleteDocumentById(db, collectionPath, row.id);
                        setRefreshKey(prev => prev + 1);
                    } catch (error: unknown) {
                        console.error('Error deleting category:', error);
                        alert(t('category.deleteFailed'));
                    }
                }
            }
        }).catch((error: unknown) => {
            console.error('Error checking related items:', error);
            // alert(t('category.deleteFailed') + ': ' + t('category.checkRelatedItemsError'));
            return;
        });
    };

    return (
        <Box sx={{ width: '100%', p: 2 }}>
            <MicrospecDataTable
                key={refreshKey}
                collection={collectionPath}
                columns={columns}
                title={t('common.categories')}
                subtitle={t('category.manageDescription')}
                // orderBy={{ field: 'order', direction: 'asc' }}
                searchable={true}
                exportable={true}
                refreshable={true}
                pageSize={25}
                maxHeight={700}
                onRowClick={(row) => navigate(`/manager/categories/update/${row.id}`)}
                actions={[
                    {
                        icon: <EditIcon />,
                        label: t('common.edit'),
                        onClick: (row) => navigate(`/manager/categories/update/${row.id}`),
                    },
                    {
                        icon: <DeleteIcon />,
                        label: t('common.delete'),
                        onClick: handleDelete,
                    },
                ]}
                globalActions={[
                    {
                        icon: <AddIcon />,
                        label: t('common.addCategory'),
                        onClick: () => navigate('/manager/categories/add'),
                        variant: 'contained',
                        color: 'primary',
                    },
                ]}
            />
        </Box>
    );
}

export default CategoriesManagementPage;